import React from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import moment from "moment";
import {
  countWarehouseReturnsByType,
  filterWarehouseReturnsByStatus,
  getWarehouseReturnPackages,
  getWarehouseReturnStatusLabel,
  getWarehouseReturnTypeLabel,
} from "util/warehouseReturnUtils";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    color: "#FFF",
    fontSize: "18px",
    fontWeight: 600,
    margin: "32px 0 12px 0",
  },
  summaryRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "16px",
  },
  summaryChip: {
    background: "#1A1A1A",
    border: "1px solid #2A2A2A",
    borderRadius: 8,
    padding: "8px 12px",
    color: "#F5F5F5",
    fontSize: "13px",
  },
  tabRow: {
    display: "flex",
    gap: "10px",
    margin: "8px 0 16px 0",
  },
  tabButton: {
    minWidth: 130,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "6px 12px",
  },
  statusPending: {
    color: theme.palette.warning.light,
    fontWeight: 600,
  },
  statusDelivered: {
    color: "#4CAF50",
    fontWeight: 600,
  },
  statusExpired: {
    color: "#F44336",
    fontWeight: 600,
  },
  emptyText: {
    color: "#ADADAD",
    fontSize: "14px",
    marginTop: "8px",
  },
  sectionWrap: {
    marginTop: 8,
    borderTop: "1px solid rgba(255,255,255,0.12)",
    paddingTop: 8,
  },
  card: {
    background: "#1E1E1E",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "12px",
    marginBottom: "16px",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "16px",
    display: "flex",
    justifyContent: "space-between",
  },
  cardHeaderCol: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  cardLabel: {
    color: "#FFF",
    fontSize: "14px",
    fontWeight: 600,
  },
  cardValue: {
    color: "#D0D0D0",
    fontSize: "13px",
    fontWeight: 400,
    marginTop: "2px",
  },
  cardDivider: {
    border: "none",
    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
    margin: "0 16px",
  },
  cardBody: {
    padding: "16px",
    display: "flex",
    gap: "16px",
  },
  cardBodyCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  cardBodyRowItem: {
    height: "24px",
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    color: "#D0D0D0",
  },
  cardFooter: {
    background: "#121212",
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "13px",
  },
  infoIcon: {
    color: "#ADADAD",
    fontSize: "18px",
    marginLeft: "6px",
    cursor: "pointer",
    "&:hover": {
      color: "#FFF",
    },
  },
  tooltipContainer: {
    padding: "4px",
  },
  tooltipLabel: {
    color: "#ADADAD",
    fontSize: "10px",
    fontWeight: 500,
    textTransform: "uppercase",
    lineHeight: 1.2,
  },
  tooltipValue: {
    color: "#FFF",
    fontSize: "13px",
    fontWeight: 600,
    marginTop: "2px",
    lineHeight: 1.2,
    marginBottom: "8px",
  },
  tooltipStatusDelivered: {
    color: "#4CAF50",
    fontSize: "13px",
    fontWeight: 600,
    marginTop: "2px",
    lineHeight: 1.2,
  },
  tooltipStatusPending: {
    color: theme.palette.warning.light,
    fontSize: "13px",
    fontWeight: 600,
    marginTop: "2px",
    lineHeight: 1.2,
  },
  tooltipStatusExpired: {
    color: "#F44336",
    fontSize: "13px",
    fontWeight: 600,
    marginTop: "2px",
    lineHeight: 1.2,
  },
}));

const DarkTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: "#1A1A1A !important",
    color: "#FFF",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    padding: "10px 14px",
    borderRadius: "6px",
    boxShadow: "0px 4px 10px rgba(0,0,0,0.5)",
    fontSize: "13px",
  },
  arrow: {
    color: "#1A1A1A !important",
  },
}))(Tooltip);

function ReturnPackageTable({ packages, classes, t }) {
  if (!packages.length) {
    return (
      <Typography className={classes.emptyText}>
        {t("warehouse_return.empty_list")}
      </Typography>
    );
  }

  // Group packages by: type, customer_name, status, timestamp (returned_at / verified_at)
  const groupPackagesIntoCards = (pkgs) => {
    const groups = {};
    for (const pkg of pkgs) {
      const timestamp = pkg.returned_at || pkg.verified_at || "";
      const dateStr = timestamp ? moment(timestamp).format("DD.MM.YYYY HH:mm") : "";
      const key = `${pkg.type}_${pkg.customer_name || ""}_${pkg.status}_${dateStr}`;
      if (!groups[key]) {
        groups[key] = {
          type: pkg.type,
          customerName: pkg.customer_name,
          status: pkg.status,
          dateStr: dateStr,
          packages: [],
        };
      }
      groups[key].packages.push(pkg);
    }
    return Object.values(groups);
  };

  const cards = groupPackagesIntoCards(packages);

  return (
    <Box>
      {cards.map((card, cardIndex) => (
        <Box key={cardIndex} className={classes.card}>
          {/* Header */}
          <Box className={classes.cardHeader}>
            <Box className={classes.cardHeaderCol}>
              <Typography className={classes.cardLabel}>{t("Delivery Type")}</Typography>
              <Typography className={classes.cardValue}>
                {getWarehouseReturnTypeLabel(card.type, t)}
              </Typography>
            </Box>
            <Box className={classes.cardHeaderCol}>
              <Typography className={classes.cardLabel}>{t("Customer Name")}</Typography>
              <Typography className={classes.cardValue}>
                {card.customerName || "-"}
              </Typography>
            </Box>
          </Box>

          <hr className={classes.cardDivider} />

          {/* Body */}
          <Box className={classes.cardBody}>
            <Box className={classes.cardBodyCol}>
              <Typography className={classes.cardLabel}>{t("warehouse_return.col.delivery_note")}</Typography>
              {card.packages.map((pkg, i) => (
                <Box key={pkg.id ?? i} className={classes.cardBodyRowItem}>
                  <span>{pkg.delivery_note_number || "-"}</span>
                  <DarkTooltip
                    title={
                      <Box className={classes.tooltipContainer}>
                        <Typography className={classes.tooltipLabel}>{t("Package ID")}</Typography>
                        <Typography className={classes.tooltipValue}>{pkg.package_id || "-"}</Typography>
                        <Typography className={classes.tooltipLabel} style={{ marginTop: "6px" }}>{t("Status")}</Typography>
                        <Typography className={pkg.status === "delivered" ? classes.tooltipStatusDelivered : pkg.status === "expired" ? classes.tooltipStatusExpired : classes.tooltipStatusPending}>
                          {getWarehouseReturnStatusLabel(pkg.status, t)}
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <InfoIcon className={classes.infoIcon} />
                  </DarkTooltip>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function TypeSummary({ countsByType, t, classes }) {
  const entries = Object.entries(countsByType).filter(([, count]) => count > 0);
  if (!entries.length) return null;

  return (
    <Box className={classes.summaryRow}>
      {entries.map(([type, count]) => (
        <Box key={type} className={classes.summaryChip}>
          {getWarehouseReturnTypeLabel(type, t)}: {count}
        </Box>
      ))}
    </Box>
  );
}

const WarehouseReturnPackagesSection = ({ pathwayStop, embedded = false, isStopMarked = false }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [returnTab, setReturnTab] = React.useState("pending");

  const allPackages = getWarehouseReturnPackages(pathwayStop);

  const effectivePackages = React.useMemo(() => {
    if (isStopMarked) {
      return allPackages;
    }
    // If stop is not marked, non-expired packages cannot be in "returned/delivered" yet; they are pending
    return allPackages.map((pkg) => {
      if (pkg?.status === "delivered") {
        return { ...pkg, status: "pending" };
      }
      return pkg;
    });
  }, [allPackages, isStopMarked]);

  const pendingPackages = filterWarehouseReturnsByStatus(effectivePackages, "pending");
  const returnedPackages = effectivePackages.filter((p) => p?.status === "delivered");
  const expiredPackages = effectivePackages.filter((p) => p?.status === "expired");

  const pendingByType = countWarehouseReturnsByType(pendingPackages, { status: "pending" });
  const returnedByType = countWarehouseReturnsByType(returnedPackages);
  const expiredByType = countWarehouseReturnsByType(expiredPackages);

  const hasPending = pendingPackages.length > 0;
  const hasReturned = returnedPackages.length > 0;
  const hasExpired = expiredPackages.length > 0;

  React.useEffect(() => {
    if (pendingPackages.length > 0) {
      setReturnTab("pending");
    } else if (returnedPackages.length > 0) {
      setReturnTab("returned");
    } else if (expiredPackages.length > 0) {
      setReturnTab("expired");
    }
  }, [
    pathwayStop?.supplier_location_id,
    pendingPackages.length,
    returnedPackages.length,
    expiredPackages.length,
  ]);

  const activeTab = React.useMemo(() => {
    if (returnTab === "pending" && hasPending) return "pending";
    if (returnTab === "returned" && hasReturned) return "returned";
    if (returnTab === "expired" && hasExpired) return "expired";

    if (hasPending) return "pending";
    if (hasReturned) return "returned";
    if (hasExpired) return "expired";
    return "pending";
  }, [returnTab, hasPending, hasReturned, hasExpired]);

  if (!allPackages.length) {
    return null;
  }

  return (
    <Box className={embedded ? undefined : classes.sectionWrap}>
      {!embedded && (
        <Typography className={classes.sectionTitle}>
          {t("warehouse_return.section_title")}
        </Typography>
      )}

      <Box className={classes.tabRow}>
        {hasPending && (
          <Button
            variant={activeTab === "pending" ? "contained" : "outlined"}
            color="primary"
            size="small"
            className={classes.tabButton}
            onClick={() => setReturnTab("pending")}
          >
            {t("Open")}: {pendingPackages.length}
          </Button>
        )}
        {hasReturned && (
          <Button
            variant={activeTab === "returned" ? "contained" : "outlined"}
            color="primary"
            size="small"
            className={classes.tabButton}
            onClick={() => setReturnTab("returned")}
          >
            {t("Returned")}: {returnedPackages.length}
          </Button>
        )}
        {hasExpired && (
          <Button
            variant={activeTab === "expired" ? "contained" : "outlined"}
            color="primary"
            size="small"
            className={classes.tabButton}
            onClick={() => setReturnTab("expired")}
          >
            {t("warehouse_return.status.expired")}: {expiredPackages.length}
          </Button>
        )}
      </Box>

      {activeTab === "pending" && hasPending && (
        <>
          <TypeSummary countsByType={pendingByType} t={t} classes={classes} />
          <ReturnPackageTable packages={pendingPackages} classes={classes} t={t} />
        </>
      )}

      {activeTab === "returned" && hasReturned && (
        <>
          <TypeSummary countsByType={returnedByType} t={t} classes={classes} />
          <ReturnPackageTable packages={returnedPackages} classes={classes} t={t} />
        </>
      )}

      {activeTab === "expired" && hasExpired && (
        <>
          <TypeSummary countsByType={expiredByType} t={t} classes={classes} />
          <ReturnPackageTable packages={expiredPackages} classes={classes} t={t} />
        </>
      )}
    </Box>
  );
};

export default WarehouseReturnPackagesSection;
